import { useState } from "react";
import { ListChecks } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useInstallments } from "@/lib/useInstallmentsCache";
import { useCommitments } from "@/lib/useCommitmentsCache";
import { useCheques } from "@/lib/useCheques";
import { ObligationsDetails } from "@/components/dashboard/ObligationsDetails";

export function NeuTodayCard({ summary }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const inst = useInstallments();
  const commits = useCommitments();
  const { items: chqs = [] } = useCheques();
  const [showDetails, setShowDetails] = useState(false);
  const ccy = summary?.currency || user?.currency || "AED";
  const daily = summary?.daily || [];
  const last7 = daily.slice(-7);
  const installRemain = (inst.active || []).reduce((s, i) => s + Math.max(0, (i.total_installments - (i.paid_count || 0))) * (i.amount || 0), 0);
  const borrowTotal = summary?.cumulative?.borrow || 0;
  const commitMonthly = commits.total_monthly || 0;
  const chequesUnpaid = chqs.filter((c) => (c.status || "pending") === "pending").reduce((s, c) => s + Number(c.amount || 0), 0);
  const totalObligations = installRemain + borrowTotal + commitMonthly + chequesUnpaid;
  const pct = (v) => totalObligations > 0 ? (v / totalObligations) * 100 : 0;

  return (
    <div className="neu p-6 sm:p-7 h-full">
      <p className="font-display text-lg font-bold neu-accent-blue mb-3">{t("myObligations")}</p>
      <div className="flex items-center gap-4">
        <div className="relative flex-1 h-14 rounded-2xl overflow-hidden"
          style={{ boxShadow: "inset 4px 4px 8px rgba(163,177,198,0.5), inset -4px -4px 8px rgba(255,255,255,0.85)" }}>
          <div className="absolute inset-0 flex" data-testid="obligations-bar">
            <div style={{ width: `${pct(commitMonthly)}%`, background: "linear-gradient(180deg,#12c98c,#10b981)" }} />
            <div style={{ width: `${pct(installRemain)}%`, background: "linear-gradient(180deg,#ffcc33,#f5b800)" }} />
            <div style={{ width: `${pct(chequesUnpaid)}%`, background: "linear-gradient(180deg,#8b5cf6,#6d28d9)" }} />
            <div style={{ width: `${pct(borrowTotal)}%`, background: "linear-gradient(180deg,#dc2626,#991b1b)" }} />
          </div>
          <div className="absolute inset-0 pointer-events-none rounded-2xl"
            style={{ boxShadow: "inset 2px 2px 4px rgba(0,0,0,0.15), inset -2px -2px 4px rgba(255,255,255,0.25)" }} />
          <div className="absolute inset-0 flex items-center justify-between px-4">
            <span className="text-xs font-bold text-white drop-shadow-md">{ccy}</span>
            <span className="font-display text-2xl font-extrabold tabular-nums text-white drop-shadow-md">{totalObligations.toFixed(0)}</span>
            <span className="text-xs text-white/70 drop-shadow-md">·</span>
          </div>
        </div>
        <button data-testid="obl-details-btn" onClick={() => setShowDetails(true)} className="w-14 h-14 rounded-2xl grid place-items-center neu-btn neu-accent-blue active:scale-95">
          <ListChecks className="w-7 h-7" strokeWidth={2} />
        </button>
      </div>
      <ObligationsDetails open={showDetails} onClose={() => setShowDetails(false)} installments={inst.active || []} ccy={ccy} borrowTotal={borrowTotal} />
      <div dir="ltr" className="mt-3 flex items-center justify-between">
        {last7.map((d, i) => {
          const dayNum = new Date(d.date).getDate();
          const isToday = i === last7.length - 1;
          return (
            <div key={d.date} className={`w-9 h-9 rounded-full grid place-items-center text-xs font-bold tabular-nums ${isToday ? "neu-fill-yellow text-white shadow-md" : "neu-sm text-slate-600"}`}>
              {dayNum}
            </div>
          );
        })}
      </div>
    </div>
  );
}
